'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { GET_STORES_MAX_DISTANCE_KM, PRODUCT_INFORMATION_MENU_ID } from '@headless-workspace/config';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { useUIContext } from '@headless-workspace/core-ui';
import {
    GlowButton,
    GlowDivider,
    GlowDropDownSearch,
    GlowInlineToast,
    GlowSegmentedPicker,
    Icons,
    PropsWithLocale,
    ToastType,
} from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { FormatHelpers } from '@headless-workspace/utils';
import { useDebounceCallback } from 'usehooks-ts';
import { CommonDI } from '../../../../DI';
import { useFavoriteStoreInfo, useLocalityDetails, useStoreAutocomplete, useUserGeolocation } from '../../../../client';
import { StoreInfoValue } from '../../../../server';
import { useProductVariantDetailContext } from './../../../../client';
import { StoreSliderDisplay } from './StoreSliderDisplay';
import { useStoreInfos } from './useStoreInfos';
import { useStoresDisplay } from './useStoresDisplay';

export enum DisplayOption {
    Map = 'map',
    List = 'list',
}

const SEARCH_INPUT_DEBOUNCE_MS = 500;
const STORES_DELIVERY_ID = 'stores-store';

export const StoresDeliveryContainer: Component<PropsWithLocale> = ({ locale }) => {
    const t = useTranslations('Modal.stores');
    const {
        menuModal: { close: closeMenu },
    } = useUIContext();

    // Retrieve the favorite store information
    const { result: favoriteStoreResult } = useFavoriteStoreInfo();
    const favoriteStoreId = unwrapResultSuccess(favoriteStoreResult)?.id ?? '';

    const [selectedDisplayOption, setSelectedDisplayOption] = useState<DisplayOption>(DisplayOption.Map);
    const [highlightedStore, setHighlightedStore] = useState<StoreInfoValue | undefined>(undefined);

    const { localityDetails, getLocalityDetails, resetLocalityDetails } = useLocalityDetails();
    const { userPosition, userAddress, getCurrentPosition } = useUserGeolocation();

    const {
        productVariantDetails: { sku },
    } = useProductVariantDetailContext();

    const {
        stores,
        status: storeQueryStatus,
        currentTime,
    } = useStoreInfos({
        locale,
        getCurrentPosition,
        localityDetails,
        userPosition,
    });

    const {
        stores: storesToDisplay,
        displayMoreStoresVisibleButton,
        setIsMoreStoresVisible,
        noStoresWithStockAvailable,
    } = useStoresDisplay({
        stores,
        productSku: sku,
        favoriteStoreId,
        highlightedStoreId: highlightedStore?.id,
    });

    // TODO: Replace all this block with Autocomplete component's behavior
    const { localitiesResults, searchLocalities, isLoading: isStoreAutocompleteLoading } = useStoreAutocomplete();

    const pickerOptions = useMemo(
        () =>
            Array.from(
                Object.keys(DisplayOption).map((option) => {
                    const displayOptionValue = DisplayOption[option as keyof typeof DisplayOption];
                    return {
                        id: displayOptionValue,
                        label: t(`label.pickerOptions.${displayOptionValue}`),
                    };
                }),
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const handleDisplayOptionChange = (option: DisplayOption) => {
        setSelectedDisplayOption(option);
    };

    const handleSearchLocationChange = useDebounceCallback(async (input: string) => {
        await searchLocalities(input);
    }, SEARCH_INPUT_DEBOUNCE_MS);

    const onSearchRightIconSubmit = (setPositionCallback: (input: string) => void) => {
        resetLocalityDetails();
        getCurrentPosition(setPositionCallback);
    };

    const storesFetched = storeQueryStatus === 'success';

    const noStoreAvailable = storesFetched && noStoresWithStockAvailable;

    const closeProductInformationModal = async () => {
        if (highlightedStore) {
            closeMenu(PRODUCT_INFORMATION_MENU_ID);
            await CommonDI.get('DeliveryStoreCookieRepository').setDeliveryStoreCookie(highlightedStore.id, locale);
        }
    };

    return (
        <>
            <section className={'flex flex-col p-1 gap-1'}>
                <GlowDropDownSearch
                    id={STORES_DELIVERY_ID}
                    defaultValue={
                        userAddress
                            ? FormatHelpers.formatAddress(
                                  userAddress.postalCode,
                                  userAddress.locality,
                                  userAddress.country,
                              )
                            : ''
                    }
                    optionsList={localitiesResults ?? []}
                    title={t('label.zipCodeOrCity')}
                    onSearchChange={handleSearchLocationChange}
                    ariaLabelInput={t('label.zipCodeOrCity')}
                    helperLabel={t('label.zipCodeOrCity')}
                    rightSearchInputIcon={Icons.Location}
                    isLoading={isStoreAutocompleteLoading}
                    onSearchRightIconSubmit={onSearchRightIconSubmit}
                    handleOptionClick={(option) => getLocalityDetails(option.value)}
                />
                {storeQueryStatus === 'init' && (
                    <GlowInlineToast
                        hasCloseBtn
                        label={{
                            text: t('toast.searchForAnAddressOrActivateGeolocation'),
                        }}
                        toastType={ToastType.Info}
                    />
                )}
                {noStoreAvailable && (
                    <GlowInlineToast
                        hasCloseBtn
                        label={{
                            text: t('toast.noStoreAvailable', {
                                distanceKm: GET_STORES_MAX_DISTANCE_KM,
                            }),
                        }}
                        toastType={ToastType.Error}
                    />
                )}

                <GlowSegmentedPicker
                    options={pickerOptions}
                    value={selectedDisplayOption}
                    onChange={handleDisplayOptionChange}
                />
            </section>
            <section className={'flex flex-col gap-1 h-full overflow-auto relative'}>
                <StoreSliderDisplay
                    stores={storesToDisplay}
                    fetched={storesFetched}
                    currentTime={currentTime ?? ''}
                    displayOption={selectedDisplayOption}
                    favoriteStoreId={favoriteStoreId}
                    setHighlightedStore={setHighlightedStore}
                    highlightedStoreId={highlightedStore?.id}
                    userPosition={userPosition}
                    localityDetails={localityDetails}
                    displayMoreStoresVisibleButton={displayMoreStoresVisibleButton}
                    setIsMoreStoresVisible={setIsMoreStoresVisible}
                />
            </section>
            <>
                <GlowDivider />
                <div className={'flex items-center justify-center p-1'}>
                    <GlowButton
                        className={'flex-1 w-full'}
                        onClick={closeProductInformationModal}
                        label={t('action.chooseStore')}
                    />
                </div>
            </>
        </>
    );
};
